import {
  Camera,
  Geometry,
  GLTFLoader,
  Mesh,
  Program,
  Quat,
  Renderer,
  RenderTarget,
  Sphere,
  Transform,
  Vec2,
  Vec3,
} from "ogl"
import { createGradient } from "./lib/gradient"
import { simplex3d } from "./lib/simplex3d"

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t
}

export type FlyingPigsParams = {
  transitionSpeed: number
  segStrength: number
  segCount: number
  blurRadius: number
  flareDist: number
  flareIterations: number
  flareFalloff: number
}

export const DEFAULT_PARAMS: FlyingPigsParams = {
  transitionSpeed: 2,
  segStrength: 1.0,
  segCount: 450,
  blurRadius: 6,
  flareDist: 0.25,
  flareIterations: 29,
  flareFalloff: 2.6,
}

type SphereInfo = {
  readonly index: number
  readonly mesh: Mesh
  light1Position: Vec3
  light1Intensity: number
  light2Position: Vec3
  light2Intensity: number
  position: Vec3
  scale: Vec3
  order: number
}

export type SceneName = "idle" | "rethink" | "amplify" | "expand" | "launch" | "pigs"

type SceneController = {
  update(self: SceneController, ctx: FlyingPigsBrandGenerator): void
  value: number
  spheres: SphereInfo[]
  time: number
  dt: number
}

const SCENES = {
  idle: {
    update(self, ctx) {
      for (const sphere of self.spheres) {
        sphere.scale.set(1)
        const a1 = self.time * 1.1 + Math.sin(self.time * 0.2) * 8
        const a2 = self.time * 1.1 + Math.sin(self.time * 0.2) * 8 + Math.PI
        sphere.light1Position.set(Math.cos(a1), Math.sin(a1), Math.sin(self.time) * 0.5 - 0.3)
        sphere.light1Intensity = 0.8
        sphere.light2Position.set(Math.cos(a2), Math.sin(a2), Math.cos(self.time) * 0.5 - 0.3)
        sphere.light2Intensity = 0.8
      }
    },
  },
  expand: {
    update(self, ctx) {
      for (const sphere of self.spheres) {
        sphere.scale.set(1)
        const t = self.time * 0.3
        const angle = t + (sphere.index / 6) * Math.PI * 2
        sphere.scale.set(lerp(0.3, 0.4, Math.sin(t * 4 + Math.PI) / 2 + 0.5))
        const distance = lerp(0.4, 1, Math.sin(Math.PI * 1 + t * 4) / 2 + 0.5)
        sphere.position.set(Math.sin(angle) * distance, Math.cos(angle) * distance, 0)
        sphere.light1Position.set(0, Math.cos(-self.time * 1.5), Math.sin(-self.time * 1.5))
        sphere.light1Position.applyQuaternion(new Quat().fromAxisAngle(new Vec3(0, 0, -1), angle))
        sphere.light1Intensity = 0.7
        sphere.light2Position.set(0, 0, 0).sub(sphere.position)
        sphere.light2Intensity = 0.5
      }
    },
  },
  amplify: {
    update(self, ctx) {
      for (const sphere of self.spheres) {
        const expansion = Math.sin(self.time + sphere.index * 0.5) / 2 + 0.5
        const scale = 1 - sphere.index / 6 + expansion * (1 - sphere.index / 6)
        sphere.scale.set(scale)
        sphere.light1Position.set(0, 1, -0.2)
        sphere.light1Intensity = 1
        sphere.light2Position.set(0, -1, -0.2)
        sphere.light2Intensity = 1
      }
    },
  },
  rethink: {
    update(self, ctx) {
      for (const sphere of self.spheres) {
        const angle = self.time + sphere.index * 0.3
        sphere.light1Position.set(Math.sin(angle * Math.PI) * 5, 0.5, Math.cos(angle * Math.PI) * 5)
        sphere.light1Intensity = 0.5
        sphere.light2Position.set(0, 0, 1)
        sphere.light2Intensity = 0.0
      }
    },
  },
  launch: {
    update(self, ctx) {
      for (const sphere of self.spheres) {
        const expansion = Math.sin(self.time * 1.5 + sphere.index * 0.5) * self.value
        sphere.position.set(0, expansion * 0.3, 0)
        sphere.light1Position.set(0, expansion, 0.5)
        sphere.light1Intensity = 0.5
        sphere.light2Position.set(0, 0, 1)
        sphere.light2Intensity = 0.0
      }
    },
  },
  pigs: {
    update(self, ctx) {
      for (const sphere of self.spheres) {
        sphere.scale.set(1)
        const t = self.time * 0.3
        const angle = t + (sphere.index / 6) * Math.PI * 2
        sphere.scale.set(0.6)
        const distance = lerp(0.9, 1, Math.sin(Math.PI * 1 + t * 4) / 2 + 0.5)
        sphere.position.set(Math.sin(angle) * distance, Math.cos(angle) * distance, 0)
        sphere.light1Position.set(0, 0, 1)
        sphere.light1Intensity = 0.5
        sphere.light2Position.set(0, 0, 1)
        sphere.light2Intensity = 0.0
        sphere.mesh.rotation.set(t + (sphere.index / 6) * Math.PI * 2, t + (sphere.index / 6) * Math.PI * 2, 0)
      }
    },
  },
} satisfies {
  [K in SceneName]: Pick<SceneController, "update">
}

export const SCENE_NAMES = Object.keys(SCENES) as SceneName[]

export class FlyingPigsBrandGenerator {
  public autoSizeContainer: HTMLElement | null = null

  public size: Vec2 = new Vec2(500, 500)
  public dpr = Math.min(1, window.devicePixelRatio) || 1

  private autoSizeObserver?: ResizeObserver

  targetScene: SceneName = "rethink"

  renderer = new Renderer({ dpr: this.dpr, antialias: false, alpha: false })
  gl = this.renderer.gl

  triangle = new Geometry(this.gl, {
    position: { size: 2, data: new Float32Array([-1, -1, 3, -1, -1, 3]) },
    uv: { size: 2, data: new Float32Array([0, 0, 2, 0, 0, 2]) },
  })

  glowProgram = new Mesh(this.gl, {
    geometry: this.triangle,
    program: new Program(this.gl, {
      vertex: baseVertex,
      fragment: glowFragment,
      uniforms: {
        tDiffuse: { value: null },
        tFrame: { value: null },
        uResolution: { value: new Vec2() },
      },
      depthTest: false,
      depthWrite: false,
    }),
  })

  flareProgram = new Mesh(this.gl, {
    geometry: this.triangle,
    program: new Program(this.gl, {
      vertex: baseVertex,
      fragment: flareFragment,
      uniforms: {
        tDiffuse: { value: null },
        tEffects: { value: null },
        uResolution: { value: new Vec2() },
        uTime: { value: 0 },
        uSegments: { value: 0 },
        uSegmentStrength: { value: 0 },
        uFlareDist: { value: 0.2 },
        uFlareIterations: { value: 30 },
        uFlareFalloff: { value: 2.0 },
      },
      depthTest: false,
      depthWrite: false,
    }),
  })

  outProgram = new Mesh(this.gl, {
    geometry: this.triangle,
    program: new Program(this.gl, {
      vertex: baseVertex,
      fragment: outputFragment,
      uniforms: {
        tDiffuse: { value: null },
        tEffects: { value: null },
        uBlurRadius: { value: 0 },
        uTime: { value: 0 },
        uResolution: { value: new Vec2() },
        uOpacity: { value: 1.0 },
      },
      depthTest: false,
      depthWrite: false,
    }),
  })

  rt = new RenderTarget(this.gl, {
    width: 10,
    height: 10,
  })
  fbo1 = new RenderTarget(this.gl, {
    width: 10,
    height: 10,
  })
  fbo2 = new RenderTarget(this.gl, {
    width: 10,
    height: 10,
  })

  sphereGeometry = new Sphere(this.gl, {
    radius: 1,
    heightSegments: 32,
    widthSegments: 32,
  })

  camera = new Camera(this.gl, {})

  private lastTime = 0
  time = 0
  dt = 0
  raf = 0

  scene: Transform
  sphereProgram: Program
  spheres: SphereInfo[] = []

  scenes: {
    [K in SceneName]: SceneController
  }

  constructor(public params: FlyingPigsParams = { ...DEFAULT_PARAMS }) {
    this.sphereProgram = new Program(this.gl, {
      vertex: sphereVertex,
      fragment: sphereFragment,
      depthTest: false,
      depthWrite: false,
      uniforms: {
        uIndex: { value: 0 },
        uProgress: { value: 0 },
        uLight1Position: { value: new Vec3() },
        uLight1Intensity: { value: 0 },
        uLight2Position: { value: new Vec3() },
        uLight2Intensity: { value: 0 },
      },
    })

    this.scene = new Transform()
    this.camera = new Camera(this.gl, {
      fov: 35,
      aspect: this.gl.canvas.width / this.gl.canvas.height,
    })
    this.camera.position.set(0, 0, 6)
    this.camera.lookAt([0, 0, 0])

    for (let i = 0; i < 6; i++) {
      const sphere = new Mesh(this.gl, {
        geometry: this.sphereGeometry,
        program: this.sphereProgram,
      })
      this.scene.addChild(sphere)
      this.spheres.push({
        index: i,
        mesh: sphere,
        position: new Vec3(),
        light1Position: new Vec3(),
        light1Intensity: 0,
        light2Position: new Vec3(),
        light2Intensity: 0,
        scale: new Vec3(1 - i / 6),
        order: i,
      })
    }

    // Initialize scenes
    this.scenes = {} as any
    for (const [key, scene] of Object.entries(SCENES)) {
      this.scenes[key as SceneName] = {
        ...scene,
        value: 0,
        time: 0,
        dt: 0,
        spheres: this.spheres.map((sphere) => ({
          ...sphere,
          light1Position: new Vec3(),
          light1Intensity: 0,
          light2Position: new Vec3(),
          light2Intensity: 0,
          position: new Vec3(),
          scale: new Vec3(1 - sphere.index / 6),
          order: sphere.index,
        })),
      }
    }
  }

  setAutoSize(container: HTMLElement | null) {
    this.autoSizeContainer = container
    if (this.autoSizeObserver) this.autoSizeObserver.disconnect()
    if (container) {
      const rect = container.getBoundingClientRect()
      this.size.set(rect.width, rect.height)
      this.didResize()
      this.autoSizeObserver = new ResizeObserver(([entry]) => {
        this.size.set(entry.contentRect.width, entry.contentRect.height)
        this.didResize()
      })
      this.autoSizeObserver.observe(container)
    }
  }

  setSize(width: number, height: number) {
    this.size.set(width, height)
    this.autoSizeContainer = null
    this.didResize()
  }

  set opacity(value: number) {
    this.outProgram.program.uniforms.uOpacity.value = value
  }

  private didResize() {
    this.renderer.setSize(this.size.x, this.size.y)
    this.dpr = window.devicePixelRatio || 1

    this.rt.setSize(this.size.x * this.dpr, this.size.y * this.dpr)
    this.fbo1.setSize(this.size.x * this.dpr, this.size.y * this.dpr)
    this.fbo2.setSize(this.size.x * this.dpr, this.size.y * this.dpr)

    this.camera.perspective({
      aspect: this.gl.canvas.width / this.gl.canvas.height,
      fov: 35,
    })
    this.camera.updateProjectionMatrix()

    this.render()
  }

  transitionScene(key: SceneName) {
    this.targetScene = key

    this.setPigModel(key === "pigs")
  }

  play() {
    this.frame(0)
  }

  private frame(time: number) {
    this.time = time
    this.dt = time === 0 ? 0 : Math.min(time - this.lastTime, 1 / 30)
    this.lastTime = time
    this.update()
    this.raf = requestAnimationFrame((time) => {
      this.frame(time / 1000)
    })
  }

  pause() {
    cancelAnimationFrame(this.raf)
  }

  update() {
    // Update controller strengths
    let cumulativeStrength = 0
    for (const [name, controller] of Object.entries(this.scenes)) {
      // Update the current scene
      const targetStrength = name === this.targetScene ? 1 : 0
      controller.value = lerp(controller.value, targetStrength, this.params.transitionSpeed * this.dt)

      // Update it
      controller.dt = this.dt
      if (controller.value > 0.01) {
        controller.time += this.dt
        controller.update(controller, this)
      } else {
        controller.value = 0
        controller.time = 0
      }

      cumulativeStrength += controller.value
    }

    // Update the actual spheres, based on strength
    for (const [sphereId, sphere] of this.spheres.entries()) {
      sphere.light1Position.set(0, 0, 0)
      sphere.light1Intensity = 0
      sphere.light2Position.set(0, 0, 0)
      sphere.light2Intensity = 0
      sphere.scale.set(0)
      sphere.position.set(0, 0, 0)
      sphere.order = 0

      for (const [name, controller] of Object.entries(this.scenes)) {
        const strength = controller.value
        if (strength == 0) continue

        const sceneSphere = controller.spheres[sphereId]
        sphere.light1Position.add(sceneSphere.light1Position.clone().multiply(strength))
        sphere.light1Intensity += sceneSphere.light1Intensity * strength
        sphere.light2Position.add(sceneSphere.light2Position.clone().multiply(strength))
        sphere.light2Intensity += sceneSphere.light2Intensity * strength
        sphere.scale.add(sceneSphere.scale.clone().multiply(strength))
        sphere.position.add(sceneSphere.position.clone().multiply(strength))
        sphere.order = Math.max(sphere.order, sceneSphere.order * strength)
      }

      sphere.light1Position.multiply(1 / cumulativeStrength)
      sphere.light1Intensity /= cumulativeStrength
      sphere.light2Position.multiply(1 / cumulativeStrength)
      sphere.light2Intensity /= cumulativeStrength
      sphere.scale.multiply(1 / cumulativeStrength)
      sphere.position.multiply(1 / cumulativeStrength)
      sphere.mesh.position.copy(sphere.position)
      sphere.mesh.scale.set(sphere.scale.x, sphere.scale.y, sphere.scale.z)
    }

    this.render()
  }

  swap() {
    const temp = this.fbo1
    this.fbo1 = this.fbo2
    this.fbo2 = temp
  }

  render() {
    this.sphereProgram.uniforms.uProgress.value = this.time
    this.spheres.sort((a, b) => a.order - b.order)
    for (const sphere of this.spheres) {
      this.sphereProgram.uniforms.uLight1Position.value = sphere.light1Position
      this.sphereProgram.uniforms.uLight1Intensity.value = sphere.light1Intensity
      this.sphereProgram.uniforms.uLight2Position.value = sphere.light2Position
      this.sphereProgram.uniforms.uLight2Intensity.value = sphere.light2Intensity
      this.gl.renderer.render({
        scene: sphere.mesh,
        camera: this.camera,
        sort: false,
        clear: sphere.order === 0,
        frustumCull: false,
        target: this.rt,
      })
    }

    // const scenes = Object.values(this.scenes)
    //   .sort((a, b) => a.value - b.value)
    //   .slice(0, 4);

    // for (const [key, scene] of Object.entries(this.scenes)) {
    //   scene.time = this.time;
    //   scene.dt = this.dt;
    //   scene.value = lerp(
    //     scene.value,
    //     key === this.targetScene ? 1 : 0,
    //     this.params.transitionSpeed * this.dt
    //   );
    //   if (scene.value < 0.0001) {
    //     scene.value = 0;
    //   }
    //   if (scene.value) scene.update();
    // }

    this.swap()
    this.glowProgram.program.uniforms.tFrame.value = this.fbo1.texture
    this.glowProgram.program.uniforms.tDiffuse.value = this.rt.texture
    this.glowProgram.program.uniforms.uResolution.value.copy(this.size)
    this.renderer.render({
      scene: this.glowProgram,
      target: this.fbo2,
    })

    this.flareProgram.program.uniforms.tDiffuse.value = this.rt.texture
    this.flareProgram.program.uniforms.tEffects.value = this.fbo2.texture
    this.flareProgram.program.uniforms.uSegments.value = this.params.segCount
    this.flareProgram.program.uniforms.uSegmentStrength.value = this.params.segStrength
    this.flareProgram.program.uniforms.uTime.value = this.time
    this.flareProgram.program.uniforms.uResolution.value.copy(this.size)
    this.flareProgram.program.uniforms.uFlareDist.value = this.params.flareDist
    this.flareProgram.program.uniforms.uFlareIterations.value = this.params.flareIterations
    this.flareProgram.program.uniforms.uFlareFalloff.value = this.params.flareFalloff
    this.renderer.render({
      scene: this.flareProgram,
      target: this.fbo1,
    })

    this.outProgram.program.uniforms.tDiffuse.value = this.fbo1.texture
    this.outProgram.program.uniforms.tEffects.value = this.fbo2.texture
    this.outProgram.program.uniforms.uTime.value = this.time
    this.outProgram.program.uniforms.uBlurRadius.value = this.params.blurRadius
    this.outProgram.program.uniforms.uResolution.value.copy(this.size)
    this.renderer.render({
      scene: this.outProgram,
    })
  }

  async setPigModel(enabled = false) {
    if (enabled) {
      const gltf = await GLTFLoader.load(this.gl, env.themePath + `/assets/pig.glb`)
      const pigGeom = gltf.meshes[0].primitives[0].geometry
      for (const sphere of this.spheres) {
        sphere.mesh.geometry = pigGeom
      }
    } else {
      for (const sphere of this.spheres) {
        sphere.mesh.geometry = this.sphereGeometry
      }
    }
  }

  dispose() {
    if (this.autoSizeObserver) {
      this.autoSizeObserver.disconnect()
    }
    cancelAnimationFrame(this.raf)
  }
}

const baseVertex = /* glsl */ `
  precision highp float;
  attribute vec2 position;
  attribute vec2 uv;
  varying vec2 vUv;

  void main () {
    vUv = uv;
    gl_Position = vec4(position, 0, 1);
  }
`

const glowFragment = /* glsl */ `
precision highp float;

varying vec2 vUv;
uniform vec2 uResolution;

uniform sampler2D tFrame;
uniform sampler2D tDiffuse;

vec4 sampleBlur(sampler2D tex, vec2 uv, float radius) {
  const float samplesX = 1.0;
  const float samplesY = 1.0;
  const float samples = 2.0;
  vec4 color = vec4(0.0);
  float total = 0.0;

  for(float x = -samplesX; x <= samplesX; x++) {
    for(float y = -samplesY; y <= samplesY; y++) {
      vec2 offset = vec2(x, y) / samples * radius;
      float weight = 1.0 - length(offset) / radius;
      color += texture2D(tex, uv + offset / uResolution) * weight;
      total += weight;
    }
  }

  return color / total;
}

void main() {
  vec2 uv = vUv;

  vec2 blurUv = (uv - 0.5) * 0.96 + 0.5;

  vec3 diffuse = texture2D(tDiffuse, uv).rgb * 0.1;
  vec3 blur = sampleBlur(tFrame, blurUv, 20.0).rgb * 0.99;

  gl_FragColor.rgb = (blur + diffuse) - (blur * diffuse) - vec3(0.002);
  gl_FragColor.a = 1.0;
}
`

const sphereVertex = /* glsl */ `
  attribute vec3 position;
  attribute vec3 normal;

  uniform mat3 normalMatrix;
  uniform mat4 modelViewMatrix;
  uniform mat4 projectionMatrix;

  varying vec3 vNormal;

  void main() {
    vNormal = normalize(normalMatrix * normal);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`

const sphereFragment = /* glsl */ `
  precision highp float;
  varying vec3 vNormal;
  uniform float uIndex;
  uniform float uProgress;
  uniform vec3 uLight1Position;
  uniform float uLight1Intensity;
  uniform vec3 uLight2Position;
  uniform float uLight2Intensity;

  const float PI = 3.141592653589793238;

  void main() {
    vec3 normal = normalize(vNormal);

    float angle = uProgress + uIndex * 1.0;

    float l1 = dot(normal, normalize(uLight1Position)) * uLight1Intensity;
    float l2 = dot(normal, normalize(uLight2Position)) * uLight2Intensity;
    float lighting = l1 + l2 - l1 * l2;
    gl_FragColor.rgb = vec3(1.0);
    gl_FragColor.g = lighting;
    gl_FragColor.a = 1.0;
  }
`

const flareFragment = /* glsl */ `
  precision highp float;
  varying vec2 vUv;
  uniform vec2 uResolution;
  uniform sampler2D tDiffuse;
  uniform sampler2D tEffects;
  uniform float uTime;

  uniform float uSegments;
  uniform float uSegmentStrength;
  uniform float uBlurRadius;

  uniform float uFlareDist;
  uniform float uFlareIterations;
  uniform float uFlareFalloff;

  const float PI = 3.141592653589793238;

  ${simplex3d}

  vec4 sampleBlur(sampler2D tex, vec2 uv, float radius) {
    const float samplesX = 1.0;
    const float samplesY = 1.0;
    const float samples = 2.0;
    vec4 color = vec4(0.0);
    float total = 0.0;

    for(float x = -samplesX; x <= samplesX; x++) {
      for(float y = -samplesY; y <= samplesY; y++) {
        vec2 offset = vec2(x, y) / samples * radius;
        float weight = 1.0 - length(offset) / radius;
        color += texture2D(tex, uv + offset / uResolution) * weight;
        total += weight;
      }
    }

    return color / total;
  }

  vec3 segment(float uvx, float totalSegments, float segmentSize) {
    float segmentIndex = floor(uvx * totalSegments) / totalSegments;
    float segmentProgress = (uvx - segmentIndex) / segmentSize;
    float segmentDistance = sin(segmentProgress * PI) / 2.0 + 0.5;
    return vec3(segmentIndex, segmentProgress, segmentDistance);
  }

  float cubicInOut(float t) {
    return t < 0.5 ? 4.0 * t * t * t : 0.5 * pow(2.0 * t - 2.0, 3.0) + 1.0;
  }

  void main() {
    float lighting = 0.0;
    vec2 uv = vUv;

    vec2 noise = vec2(snoise(vec3(uv * uResolution, uTime) * 0.2), snoise(vec3(uv * uResolution, uTime + 5.0) * 0.2));
    vec2 warpUV = uv;
    vec4 effects = texture2D(tEffects, warpUV + noise * 0.007);
    effects.rgb *= pow(effects.a, 1.2) * 0.4;

    const float MAX_ITERATIONS = 20.0;
    for (float i = 0.0; i < MAX_ITERATIONS; i += 1.0) {
      if (i <= uFlareIterations) {
        float flare = (i / uFlareIterations);
        float flareDist = flare * uFlareDist;
        float flarePower = pow(1.0 - i / uFlareIterations, uFlareFalloff);
        warpUV = (uv - 0.5) * (1.0 - flareDist) + 0.5;

        float totalSegments = uSegments;
        float segmentStrength = uSegmentStrength * flarePower;
        float segmentSize = 1.0 / totalSegments;

        vec3 segmentX = segment(warpUV.x, totalSegments, segmentSize);
        float segmentIndex = segmentX.x;
        float segmentProgress = segmentX.y;
        float segmentDistance = segmentX.z;
        float maxSegmentDistance = segmentDistance;
        warpUV.x = segmentIndex + segmentSize * mix(segmentProgress, cubicInOut(1.0 - segmentProgress), segmentStrength);

        vec4 diffuse = texture2D(tDiffuse, warpUV);

        vec2 params = diffuse.rb + effects.rb;

        float pass = diffuse.g * mix(1.0, segmentDistance, segmentStrength) * flarePower;

        // pass = (lighting + pass) - (lighting * pass);
        lighting = max(lighting, pass);
      }
    }
    lighting = lighting + effects.g;
    
    gl_FragColor = vec4(vec3(lighting), 1.0);
  }
`

const outputFragment = /* glsl */ `
  precision highp float;
  varying vec2 vUv;
  uniform vec2 uResolution;
  uniform sampler2D tDiffuse;
  uniform sampler2D tEffects;
  uniform float uTime;
  uniform float uOpacity;

  uniform float uSegments;
  uniform float uSegmentStrength;
  uniform float uBlurRadius;

  const float PI = 3.141592653589793238;

  // Reads a blurred version of the scene render buffer at the given UV
  vec4 sampleBlur(sampler2D tex, vec2 uv, float radius) {
    const float samplesX = 1.0;
    const float samplesY = 1.0;
    const float samples = 2.0;
    vec4 color = vec4(0.0);
    float total = 0.0;

    for(float x = -samplesX; x <= samplesX; x++) {
      for(float y = -samplesY; y <= samplesY; y++) {
        vec2 offset = vec2(x, y) / samples * radius;
        float weight = 1.0 - length(offset) / radius;
        color += texture2D(tex, uv + offset / uResolution) * weight;
        total += weight;
      }
    }

    return color / total;
  }

  vec3 segment(float uvx, float totalSegments, float segmentSize) {
    float segmentIndex = floor(uvx * totalSegments) / totalSegments;
    float segmentProgress = (uvx - segmentIndex) / segmentSize;
    float segmentDistance = sin(segmentProgress * PI) / 2.0 + 0.5;
    return vec3(segmentIndex, segmentProgress, segmentDistance);
  }

  ${createGradient("sampleGradient1", [
    [0.0, "#0060D1"],
    [0.5, "#7646FF"],
    [0.9, "#0DC8D8"],
  ])}

  ${simplex3d}

  vec3 vibrant(vec3 color) {
    float m = max(max(color.r, color.g), color.b);
    float power = 1.3;
    return vec3(pow(color.r / m, power), pow(color.g / m, power), pow(color.b / m, power));
  }

  vec3 colorize(float intensity) {
    return sampleGradient1(intensity);
  }

  float cubicInOut(float t) {
    return t < 0.5 ? 4.0 * t * t * t : 0.5 * pow(2.0 * t - 2.0, 3.0) + 1.0;
  }

  vec4 deband(vec4 color) {
    vec3 magic = vec3(0.06711056, 0.00583715, 52.9829189);
    return vec4(color + fract(magic.z * fract(dot(gl_FragCoord.xy, magic.xy))) / 255.0);
  }

  void main() {
    vec3 color;
    vec2 uv = vUv;

    float lighting = sampleBlur(tDiffuse, uv, uBlurRadius).r;

    color = colorize(lighting) * lighting;

    vec4 outColor = vec4(color, 1.0) * uOpacity;
    // color = vec3(lighting);
    gl_FragColor = deband(outColor);
  }
`
